import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist/legacy/build/pdf.mjs';
import { Alert } from './UI';

type PdfJs = typeof import('pdfjs-dist/legacy/build/pdf.mjs');

/** Zusatzdaten fuer pdf.js, ausgeliefert vom pdfjsAssets-Plugin in vite.config.ts. */
const ASSET_BASE = `${import.meta.env.BASE_URL}pdfjs/`;
/** Obergrenze je Seitenbild: iOS Safari verweigert zu grosse Canvas-Flaechen und rendert dann leer. */
const MAX_PAGE_PIXELS = 4_000_000;
/** Seiten werden erst kurz vor dem Hineinscrollen gerendert. */
const RENDER_MARGIN = '800px 0px';

let pdfJsPromise: Promise<PdfJs> | null = null;

/**
 * Legacy-Build, damit auch aeltere iOS- und Android-Browser unterstuetzt sind; wird erst beim ersten Oeffnen einer
 * PDF-Vorschau nachgeladen.
 */
function loadPdfJs(): Promise<PdfJs> {
  if (!pdfJsPromise) {
    pdfJsPromise = Promise.all([
      import('pdfjs-dist/legacy/build/pdf.mjs'),
      import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
    ])
      .then(([pdfjs, worker]) => {
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
        return pdfjs;
      })
      .catch((err) => {
        pdfJsPromise = null;
        throw err;
      });
  }
  return pdfJsPromise;
}

interface PdfPreviewProps {
  /** Object-URL des heruntergeladenen PDFs. */
  url: string;
  title: string;
  /** Hoehe/Rahmen des Scrollbereichs, z.B. "h-[70vh]". */
  className?: string;
}

/**
 * PDF-Vorschau mit pdf.js statt <iframe>: Handy-Browser zeigen PDFs im iframe gar nicht (Android) oder nur die erste
 * Seite (iOS). Hier wird jede Seite selbst gerendert und untereinander scrollbar angezeigt.
 */
export function PdfPreview({ url, title, className }: PdfPreviewProps) {
  const { t } = useTranslation();
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let loadingTask: ReturnType<PdfJs['getDocument']> | null = null;
    setPdf(null);
    setError('');

    (async () => {
      try {
        const [pdfjs, data] = await Promise.all([loadPdfJs(), fetch(url).then((res) => res.arrayBuffer())]);
        if (cancelled) return;
        loadingTask = pdfjs.getDocument({
          data,
          standardFontDataUrl: `${ASSET_BASE}standard_fonts/`,
          wasmUrl: `${ASSET_BASE}wasm/`,
          iccUrl: `${ASSET_BASE}iccs/`,
          cMapUrl: `${ASSET_BASE}cmaps/`,
          cMapPacked: true,
        });
        const doc = await loadingTask.promise;
        if (!cancelled) setPdf(doc);
      } catch {
        if (!cancelled) setError(t('common:pdfPreview.error'));
      }
    })();

    return () => {
      cancelled = true;
      loadingTask?.destroy();
    };
  }, [url, t]);

  return (
    <div className={clsx('overflow-auto', className)} role="document" aria-label={title}>
      {error ? (
        <div className="p-3"><Alert type="error" message={error} /></div>
      ) : !pdf ? (
        <div className="flex h-full min-h-[12rem] items-center justify-center gap-3 text-sm text-dark-300" role="status">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary-300/40 border-t-primary-300" />
          {t('common:pdfPreview.loading')}
        </div>
      ) : (
        <div className="space-y-3 p-2 sm:p-3">
          {Array.from({ length: pdf.numPages }, (_, index) => (
            <PdfPage
              key={index}
              pdf={pdf}
              pageNumber={index + 1}
              label={pdf.numPages > 1 ? t('common:pdfPreview.page_label', { number: index + 1, count: pdf.numPages }) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface PdfPageProps {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  label?: string;
}

function PdfPage({ pdf, pageNumber, label }: PdfPageProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [visible, setVisible] = useState(false);
  // Hoehe/Breite fuer den Platzhalter, bis die echte Seitengroesse bekannt ist (A4 hochkant).
  const [aspect, setAspect] = useState(Math.SQRT2);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;
    // Auf 100 px aufrunden, damit nicht jede kleine Groessenaenderung neu rendert.
    const measure = () => setWidth(Math.ceil(element.clientWidth / 100) * 100);
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    measure();
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          intersectionObserver.disconnect();
        }
      },
      { rootMargin: RENDER_MARGIN }
    );
    intersectionObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!visible || width === 0) return;
    let cancelled = false;
    let renderTask: RenderTask | null = null;

    (async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const base = page.getViewport({ scale: 1 });
        setAspect(base.height / base.width);

        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        let scale = (width / base.width) * pixelRatio;
        if (base.width * base.height * scale * scale > MAX_PAGE_PIXELS) {
          scale = Math.sqrt(MAX_PAGE_PIXELS / (base.width * base.height));
        }
        const viewport = page.getViewport({ scale });

        // Jedes Mal eine neue Canvas: pdf.js verbietet parallele Renderings in dieselbe Canvas, und die alte Seite
        // bleibt sichtbar, bis die neue fertig ist.
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        renderTask = page.render({ canvas, viewport });
        await renderTask.promise;
        if (cancelled) return;

        // Als Bild statt Canvas behalten: der Browser darf ausserhalb des Sichtbereichs dekodierte Bilder verwerfen,
        // Canvas-Speicher dagegen nicht - bei vielen Seiten laeuft iOS Safari sonst in sein Speicherlimit.
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
        canvas.width = 0;
        canvas.height = 0;
        if (!cancelled && blob) setImageUrl(URL.createObjectURL(blob));
      } catch {
        // Abgebrochene Renderings (Groessenaenderung, Schliessen) sind erwartbar; eine defekte Seite bleibt leer.
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNumber, visible, width]);

  useEffect(() => () => { if (imageUrl) URL.revokeObjectURL(imageUrl); }, [imageUrl]);

  return (
    <figure>
      <div ref={wrapperRef} className="overflow-hidden rounded bg-white shadow" style={{ aspectRatio: `1 / ${aspect}` }}>
        {imageUrl && <img src={imageUrl} alt={label ?? ''} className="block h-full w-full" draggable={false} />}
      </div>
      {label && <figcaption className="mt-1 text-center text-xs text-dark-400">{label}</figcaption>}
    </figure>
  );
}
